import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, type AlertsResponse, type Alert } from '@/lib/api';
import { formatTimeAgo, formatMoney, formatWalletAddress, getSeverityVariant } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

export function Alerts() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [alerts, setAlerts] = useState<Alert[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
    });

    const [minScore, setMinScore] = useState(searchParams.get('minScore') || 'all');
    const [severity, setSeverity] = useState(searchParams.get('severity') || 'all');
    const [category, setCategory] = useState(searchParams.get('category') || 'all');

    useEffect(() => {
        fetchAlerts();
    }, [searchParams]);

    async function fetchAlerts() {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams(searchParams);
            if (!params.has('limit')) params.set('limit', '20');

            const response = await api.get<AlertsResponse>(`/api/alerts?${params}`);

            if (response.success) {
                setAlerts(response.data.alerts);
                setPagination(response.data.pagination);
            } else {
                setError('Failed to load alerts');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load alerts');
        } finally {
            setLoading(false);
        }
    }

    function applyFilters() {
        const params = new URLSearchParams();
        if (minScore !== 'all') params.set('minScore', minScore);
        if (severity !== 'all') params.set('severity', severity);
        if (category !== 'all') params.set('category', category);
        params.set('page', '1');
        setSearchParams(params);
    }

    function goToPage(page: number) {
        const params = new URLSearchParams(searchParams);
        params.set('page', page.toString());
        setSearchParams(params);
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold">Whale Alerts</h1>
                <p className="text-muted-foreground mt-1">
                    Real-time alerts when sharp wallets make significant trades
                </p>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
                <Select value={minScore} onValueChange={setMinScore}>
                    <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Min Score" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Scores</SelectItem>
                        <SelectItem value="80">80+ (Critical)</SelectItem>
                        <SelectItem value="60">60+ (High)</SelectItem>
                        <SelectItem value="40">40+ (Medium)</SelectItem>
                    </SelectContent>
                </Select>

                <Select value={severity} onValueChange={setSeverity}>
                    <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Severity" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Severities</SelectItem>
                        <SelectItem value="critical">🚨 Critical</SelectItem>
                        <SelectItem value="high">🟠 High</SelectItem>
                        <SelectItem value="medium">🟢 Medium</SelectItem>
                        <SelectItem value="low">⚪ Low</SelectItem>
                    </SelectContent>
                </Select>

                <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        <SelectItem value="geopolitics">Geopolitics</SelectItem>
                        <SelectItem value="crypto">Crypto</SelectItem>
                        <SelectItem value="elections">Elections</SelectItem>
                        <SelectItem value="economics">Economics</SelectItem>
                        <SelectItem value="sports">Sports</SelectItem>
                    </SelectContent>
                </Select>

                <Button variant="secondary" onClick={applyFilters}>
                    Apply Filters
                </Button>

                <Button variant="ghost" size="icon" onClick={fetchAlerts} disabled={loading}>
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
            </div>

            {/* Content */}
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
                </div>
            ) : error ? (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12">
                        <span className="text-4xl mb-4">⚠️</span>
                        <p className="text-muted-foreground mb-4">{error}</p>
                        <Button variant="secondary" onClick={fetchAlerts}>
                            Try Again
                        </Button>
                    </CardContent>
                </Card>
            ) : alerts.length === 0 ? (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12">
                        <span className="text-4xl mb-4">🐋</span>
                        <p className="font-medium mb-1">No alerts yet</p>
                        <p className="text-muted-foreground text-sm">
                            Whale alerts will appear here when detected
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <>
                    <Card className="overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[100px]">Time</TableHead>
                                    <TableHead className="w-[80px]">Score</TableHead>
                                    <TableHead>Market</TableHead>
                                    <TableHead className="w-[180px]">Wallet</TableHead>
                                    <TableHead className="w-[120px]">Side</TableHead>
                                    <TableHead className="w-[100px] text-right">Size</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {alerts.map((alert) => (
                                    <TableRow
                                        key={alert.id}
                                        className="cursor-pointer"
                                        onClick={() => navigate(`/alerts/${alert.id}`)}
                                    >
                                        <TableCell className="text-muted-foreground text-sm">
                                            {formatTimeAgo(alert.createdAt)}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={getSeverityVariant(alert.score)} className="font-mono">
                                                {alert.score}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <div className="max-w-[300px]">
                                                <div className="font-medium truncate">
                                                    {alert.market.question || alert.market.slug || alert.marketId}
                                                </div>
                                                {alert.market.category && (
                                                    <span className="text-xs text-muted-foreground capitalize">
                                                        {alert.market.category}
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className="font-mono text-sm text-muted-foreground hover:text-primary cursor-pointer"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/wallet/${alert.walletAddress}`);
                                                    }}
                                                >
                                                    {formatWalletAddress(alert.walletAddress)}
                                                </span>
                                                {(alert.wallet.classification === 'whale' ||
                                                    alert.wallet.classification === 'sharp') && (
                                                        <Badge
                                                            variant={alert.wallet.classification === 'whale' ? 'whale' : 'sharp'}
                                                            className="text-xs"
                                                        >
                                                            {alert.wallet.classification === 'whale' ? '🐋' : '🎯'}
                                                        </Badge>
                                                    )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span
                                                className={`font-semibold ${alert.trade.side === 'YES' ? 'text-green-400' : 'text-red-400'
                                                    }`}
                                            >
                                                {alert.trade.side}
                                            </span>
                                            <span className="text-muted-foreground text-sm ml-2">
                                                @ {(alert.trade.price * 100).toFixed(0)}¢
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right font-mono font-medium">
                                            {formatMoney(alert.trade.size)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Card>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => goToPage(pagination.page - 1)}
                                disabled={pagination.page <= 1}
                            >
                                <ChevronLeft className="w-4 h-4 mr-1" />
                                Prev
                            </Button>
                            <span className="text-sm text-muted-foreground px-4">
                                Page {pagination.page} of {pagination.totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => goToPage(pagination.page + 1)}
                                disabled={pagination.page >= pagination.totalPages}
                            >
                                Next
                                <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
